import { useEffect, useState } from 'react';
import { AUTH_URL } from '../../../../services/apiConfig';
import { fetchJsonWithAuth } from '../../../../services/authApi';
import { normalizeOption } from '../utils/popupUtils';

function fieldSupportsMasterOptions(field) {
  return ['select', 'multi-select', 'search-select', 'radio'].includes(field?.type)
    && field?.dataSource === 'master'
    && field?.masterName;
}

function unwrapMasterRows(json) {
  const payload = json?.data ?? json;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.records)) return payload.records;
  return [];
}

function isClientSubmissionStatusMaster(field) {
  const masterName = String(field?.masterName ?? '').trim().toLowerCase();
  return masterName === 'clientsubmissions' || masterName === 'clientsubmissionstatus';
}

function popupMasterOptionField(field) {
  if (!isClientSubmissionStatusMaster(field)) return field;
  return {
    ...field,
    labelKey: field.labelKey || 'clientSubmissionStatusText',
    valueKey: field.valueKey || 'clientSubmissionStatusText',
  };
}

function normalizeMasterOption(item, field) {
  const option = normalizeOption(item, popupMasterOptionField(field));

  // Change Status must submit the human-readable client submission status, not
  // the master's numeric uniqueId. The common /masters/dropdown response is
  // normally { label, value }, where value is that numeric id, so force the
  // option value to the visible label for this status master only.
  if (isClientSubmissionStatusMaster(field)) {
    return { ...option, value: String(option.label ?? '') };
  }

  return option;
}

/**
 * Manages dynamic option fetching and extra-data loading for DynamicPopup.
 *
 * Uses AbortController (latest-request-wins) so that state is never updated
 * from a response that arrived after the popup closed or the deps changed.
 *
 * @param {Object} params
 * @param {boolean} params.open
 * @param {Object|null} params.config
 * @param {Object} params.api - { getOptions?, loadExtraData? }
 * @param {Array} params.fields
 * @param {Object} params.popupContext
 * @param {Object} params.formValues
 * @returns {{ dynamicOptions: Object, setDynamicOptions: Function, extraData: * }}
 */
export function usePopupOptions({ open, config, api, fields, popupContext, formValues }) {
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [extraData, setExtraData] = useState(null);

  // Clear stale data when the popup closes so the next open starts fresh
  useEffect(() => {
    if (!open) {
      setDynamicOptions({});
      setExtraData(null);
    }
  }, [open]);

  // Fetch options that have no form-value dependency (runs once on open)
  useEffect(() => {
    if (!open || !config || !api.getOptions) return;

    const controller = new AbortController();

    fields.forEach((field) => {
      if (!field.fetchOptionsKey || field.fetchOptionsParamKey) return;

      Promise.resolve(api.getOptions(field, undefined, popupContext))
        .then((items) => {
          if (controller.signal.aborted) return;
          setDynamicOptions((prev) => ({ ...prev, [field.name]: items }));
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.error(`[DynamicPopup] Failed to load options for field "${field.name}":`, err);
        });
    });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api, config, fields, popupContext]);

  // Fetch master-backed dropdown options declared directly on popup fields.
  useEffect(() => {
    if (!open || !config) return;

    const masterFields = fields.filter(fieldSupportsMasterOptions);
    if (!masterFields.length) return;

    const controller = new AbortController();

    masterFields.forEach((field) => {
      const params = new URLSearchParams({
        masterName: field.masterName,
        offset: '0',
        limit: String(field.limit ?? 50),
      });

      fetchJsonWithAuth(AUTH_URL, `/masters/dropdown?${params.toString()}`)
        .then((json) => {
          if (controller.signal.aborted) return;
          const options = unwrapMasterRows(json).map((item) => normalizeMasterOption(item, field));
          setDynamicOptions((prev) => ({ ...prev, [field.name]: options }));
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.error(`[DynamicPopup] Failed to load master options for field "${field.name}":`, err);
        });
    });

    return () => controller.abort();
  }, [open, config, fields]);

  // Fetch options that depend on another form field value (re-runs when that value changes)
  useEffect(() => {
    if (!open || !config || !api.getOptions) return;

    const paramFields = fields.filter((f) => f.fetchOptionsKey && f.fetchOptionsParamKey);
    if (!paramFields.length) return;

    const controller = new AbortController();

    paramFields.forEach((field) => {
      const paramValue =
        formValues[field.fetchOptionsParamKey] ?? popupContext[field.fetchOptionsParamKey];
      const fetchParams = { [field.fetchOptionsParamKey]: paramValue };

      Promise.resolve(api.getOptions(field, fetchParams, popupContext))
        .then((items) => {
          if (controller.signal.aborted) return;
          setDynamicOptions((prev) => ({ ...prev, [field.name]: items }));
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.error(
            `[DynamicPopup] Failed to load param-dependent options for "${field.name}":`,
            err,
          );
        });
    });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api, config, fields, formValues, popupContext]);

  // Load extra data (e.g. existing recruiters for the others-assign popup)
  useEffect(() => {
    if (!open || !config || !api.loadExtraData) return;

    const controller = new AbortController();

    Promise.resolve(api.loadExtraData(popupContext))
      .then((data) => {
        if (controller.signal.aborted) return;
        setExtraData(data);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('[DynamicPopup] Failed to load extra data:', err);
        setExtraData(null);
      });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api, config, popupContext]);

  return { dynamicOptions, setDynamicOptions, extraData };
}
